import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { useTranslation } from "react-i18next";

import Loading from "../../components/Loading";

import {
  listCommodities,
  listDeposits,
  listDepositsReset,
  listMineAggregators,
} from "../../actions";
import { displayErrorMessage } from "../../utils";
import { toFilterString } from "../../utils/filters";
import { orderByLocalName } from "../../utils/i18n";
import { Aggregator } from "../AggregatorDetailsModal/types";
import { StockStatusTabs } from "./components/StockStatusTabs";
import { Deposit } from "./DepositCard/deposit-dtos";
import { Filters } from "./Filters";
import { DepositsList } from "./DepositsList";
import { DepositFilterValues, DepositListParams } from "./types";
import { useStockStatusCounts } from "./useStockStatusCounts";
import {
  buildDepositParams,
  DEFAULT_DEPOSIT_FILTERS,
  DEPOSITS_PAGE_SIZE,
  toStockStatusOption,
} from "./utils";
import { Groceries, RefreshCw, Wheat } from "lucide-react";

import { type AggregatorAreaOption } from "@/components/AggregatorAreaFilter";
import { EmptyState } from "@/components/EmptyState";
import { WFPButton } from "@/components/ui/button";

interface CommodityOption {
  id: number;
  name: string;
  name_translations?: { en: string };
}

interface StateProps {
  deposits: Deposit[];
  totalItems: number;
  isFetchingDeposits: boolean;
  errorMessageDeposits: string;
  commodities: CommodityOption[];
  aggregators: (Aggregator & AggregatorAreaOption)[];
}

interface DispatchProps {
  listDeposits: (params: DepositListParams) => void;
  listDepositsReset: () => void;
  listCommodities: () => void;
  listMineAggregators: () => void;
}

type DepositsProps = StateProps & DispatchProps & RouteComponentProps & { readOnly?: boolean };

const buildInitialFilters = (state: unknown): DepositFilterValues => {
  const s = (state ?? {}) as Partial<Record<keyof DepositFilterValues, unknown>>;
  const legacy = (state ?? {}) as { aggregator_id?: unknown };
  return {
    stock_status: toStockStatusOption(s.stock_status),
    commodity_id: toFilterString(s.commodity_id),
    quality: toFilterString(s.quality),
    aggregator_ids: toFilterString(s.aggregator_ids ?? legacy.aggregator_id),
  };
};

export const Deposits = ({
  deposits,
  totalItems,
  isFetchingDeposits,
  errorMessageDeposits,
  commodities,
  aggregators,
  history,
  readOnly = false,
  listDeposits,
  listDepositsReset,
  listCommodities,
  listMineAggregators,
}: DepositsProps) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<DepositFilterValues>(() =>
    buildInitialFilters(history.location.state)
  );
  const [page, setPage] = useState(1);
  const stockStatusCounts = useStockStatusCounts();

  useEffect(() => {
    listMineAggregators();
    listCommodities();

    return () => listDepositsReset();
  }, [listMineAggregators, listCommodities, listDepositsReset]);

  useEffect(() => {
    listDeposits(buildDepositParams(filters, page));
  }, [filters, page, listDeposits]);

  const stockStatus = toStockStatusOption(filters.stock_status);

  const handleFilterChange = (patch: Partial<DepositFilterValues>) => {
    setPage(1);
    setFilters(current => ({ ...current, ...patch }));
  };

  const handleClearFilters = () => {
    setPage(1);
    setFilters(DEFAULT_DEPOSIT_FILTERS);
  };

  const refreshList = () => {
    stockStatusCounts.refresh();
    listDeposits(buildDepositParams(filters, page));
  };

  const handleAggregatorDetails = (aggregatorId: number) => {
    history.push(
      readOnly
        ? `/wfpManager/aggregators/${aggregatorId}`
        : `/representative/market/aggregator/${aggregatorId}`
    );
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-y-3">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-blue-600 bg-blue-100">
              <Wheat />
            </span>
            <h1 className="font-semibold text-2xl">{t("farmersMarket.title")}</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            {t(readOnly ? "farmersMarket.managerSubtitle" : "farmersMarket.subtitle")}
          </p>
        </div>
        <WFPButton type="button" variant="secondary" onClick={refreshList}>
          <RefreshCw className="size-4" aria-hidden="true" />
          {t("marketOfferView.refresh")}
        </WFPButton>
      </div>
      <div className="flex flex-col gap-6">
        <Filters
          commodities={commodities}
          aggregators={aggregators}
          filters={filters}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />

        <StockStatusTabs
          value={stockStatus}
          onChange={stock_status => handleFilterChange({ stock_status })}
          counts={stockStatusCounts.counts}
          isLoading={stockStatusCounts.isLoading}
        />

        {errorMessageDeposits && displayErrorMessage(errorMessageDeposits)}

        {isFetchingDeposits ? (
          <Loading />
        ) : deposits.length ? (
          <>
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t(`farmersMarket.stockShownRange.${stockStatus}`, {
                count: totalItems,
                from: (page - 1) * DEPOSITS_PAGE_SIZE + 1,
                to: (page - 1) * DEPOSITS_PAGE_SIZE + deposits.length,
              })}
            </p>
            <DepositsList
              deposits={deposits}
              totalItems={totalItems}
              page={page}
              onPageChange={setPage}
              onClickAggregatorDetails={handleAggregatorDetails}
              readOnly={readOnly}
            />
          </>
        ) : (
          <EmptyState
            icon={Groceries}
            title={t(
              stockStatus === "FOR_SALE"
                ? "farmersMarket.noDeposits"
                : `farmersMarket.noStock.${stockStatus}`
            )}
            description={
              readOnly && stockStatus === "FOR_SALE"
                ? t("home.manager.overview.highlights.fresh.emptyDescription")
                : undefined
            }
          />
        )}
      </div>
    </main>
  );
};

interface RequestState<T> {
  isFetching: boolean;
  errorMessage: string;
  data: T;
}

interface RootState {
  listDeposits: RequestState<{ results?: Deposit[]; count?: number }>;
  listCommodities: RequestState<{ results?: CommodityOption[] }>;
  listMineAggregators: RequestState<{ results?: (Aggregator & AggregatorAreaOption)[] }>;
}

export const mapStateToProps = (state: RootState): StateProps => ({
  deposits: state.listDeposits.data.results ?? [],
  totalItems: state.listDeposits.data.count ?? 0,
  isFetchingDeposits: state.listDeposits.isFetching,
  errorMessageDeposits: state.listDeposits.errorMessage,
  commodities: orderByLocalName(state.listCommodities.data.results) as CommodityOption[],
  aggregators: state.listMineAggregators.data.results ?? [],
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listDeposits: params => dispatchAction(dispatch, listDeposits(params)),
  listDepositsReset: () => dispatchAction(dispatch, listDepositsReset()),
  listCommodities: () => dispatchAction(dispatch, listCommodities({ page: 1, page_size: 100 })),
  // `with_location` adds each aggregator's admin area chain, shown (and searched) in the
  // "Aggregator and Admin Area" filter.
  listMineAggregators: () => dispatchAction(dispatch, listMineAggregators({ with_location: true })),
});

export const ConnectedDeposits = connect(mapStateToProps, mapDispatchToProps)(withRouter(Deposits));

export const WfpManagerDeposits = () => <ConnectedDeposits readOnly />;
